import { apiClient } from '../api/apiClient';

export interface ConsultationRequest {
  id: number;
  user_question?: string;
  context?: string;
  request_type?: string;
  status: string;
  created_at: string;
  updated_at?: string;
  ai_summary?: string;
  doctor_notes?: string;
  user_name?: string;
}

export interface ConsultationRequestUpdate {
  status: string;
  doctor_notes?: string;
}

export interface ClinicalReport {
  id: number;
  chat_session_id?: number;
  user_id: number;
  report_data?: any;
  created_at: string;
}

export interface Prescription {
  medication_name: string;
  dosage?: string;
  frequency?: string;
  instructions?: string;
  prescribed_by?: string;
}

export interface ChatMessage {
  id: number;
  role: 'user' | 'assistant';
  content: string;
  created_at?: string;
}

export interface ConsultationRequestWithDetails extends ConsultationRequest {
  clinical_report?: ClinicalReport;
  doctor?: any;
  chat_messages?: ChatMessage[];
}

export interface AIRequestInsights {
  total: number;
  pending: number;
  completed: number;
  declined: number;
}

class AIRequestsService {
  /**
   * Get all consultation requests for the doctor
   */
  async getConsultationRequests(statusFilter?: string): Promise<ConsultationRequest[]> {
    const params = new URLSearchParams();
    if (statusFilter) {
      params.append('status_filter', statusFilter);
    }

    const response = await apiClient.get<ConsultationRequest[]>(
      `/api/v1/doctors/my-consultation-requests${params.toString() ? `?${params.toString()}` : ''}`
    );
    return response.data;
  }

  /**
   * Update consultation request status (accept/reject/complete)
   */
  async updateRequestStatus(
    requestId: number,
    status: 'pending' | 'rejected' | 'completed',
    doctorNotes?: string,
    prescriptions?: Prescription[],
    labTests?: Array<{test_name: string, reason?: string, instructions?: string}>
  ): Promise<ConsultationRequest> {
    const response = await apiClient.patch<ConsultationRequest>(
      `/api/v1/doctors/consultation-requests/${requestId}/status`,
      {
        status,
        doctor_notes: doctorNotes,
        prescriptions: prescriptions,
        lab_tests: labTests,
      }
    );
    return response.data;
  }

  /**
   * Get consultation request with clinical report details
   */
  async getConsultationDetails(requestId: number): Promise<ConsultationRequestWithDetails> {
    const response = await apiClient.get<ConsultationRequestWithDetails>(
      `/api/v1/doctors/consultation-requests/${requestId}/with-clinical-report`
    );
    return response.data;
  }

  /**
   * Generate AI summary for a consultation request
   */
  async generateSummary(requestId: number): Promise<ConsultationRequest> {
    const response = await apiClient.post<ConsultationRequest>(
      '/api/v1/doctors/generate-summary',
      {
        request_id: requestId,
      }
    );
    return response.data;
  }

  /**
   * Save prescriptions for a consultation request
   */
  async savePrescriptions(requestId: number, prescriptions: Prescription[]): Promise<any> {
    const response = await apiClient.post(
      `/api/v1/doctors/consultation-requests/${requestId}/prescriptions`,
      {
        prescriptions: prescriptions.map((p) => ({
          medication_name: p.medication_name,
          dosage: p.dosage || '',
          frequency: p.frequency || '',
          instructions: p.instructions || '',
          prescribed_by: p.prescribed_by || '',
        })),
      }
    );
    return response.data;
  }

  /**
   * Calculate insights from requests
   */
  calculateInsights(requests: ConsultationRequest[]): AIRequestInsights {
    return {
      total: requests.length,
      pending: requests.filter((r) => {
        const statusLower = r.status?.toLowerCase() || '';
        return statusLower === 'pending' || statusLower === 'requested';
      }).length,
      completed: requests.filter((r) => {
        const statusLower = r.status?.toLowerCase() || '';
        return statusLower === 'completed';
      }).length,
      declined: requests.filter((r) => {
        const statusLower = r.status?.toLowerCase() || '';
        return statusLower === 'rejected' || statusLower === 'declined';
      }).length,
    };
  }
}

export const aiRequestsService = new AIRequestsService();

